import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserFromVerification } from "../../store/slices/authSlice";
import ReferralCodeStep from "../../components/auth/ReferralCodeStep";
import AccountTypePrelude from "../../components/auth/AccountTypePrelude";
import Step1Enhanced from "../../components/auth/Step1Enhanced";
import Step2Enhanced from "../../components/auth/Step2Enhanced";
import Step3Enhanced from "../../components/auth/Step3Enhanced";
import Step4Enhanced from "../../components/auth/Step4Enhanced";
import Step5Enhanced from "../../components/auth/Step5Enhanced";
import Step6 from "../../components/auth/Step6";
import authService from "../../components/services/AuthService";
import Images from "../../utils/Images";

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [accountType, setAccountType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    hasReferralCode: null,
    referralCode: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneCountry: "233",
    accountDisplayName: "",
    companyWebsite: "",
    linkedinPage: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    region: "",
    zipCode: "",
    country: "ghana",
    timezone: "africa/accra",
    currency: "ghs",
    idType: "",
    idNumber: "",
    otherIdName: "",
    promotionChannels: {},
    contentCategories: [],
    targetAudiences: [],
    postingFrequency: "",
    weeklyPostCount: "",
    hasAffiliateExperience: null,
    previousAffiliatePlatforms: "",
    motivationToJoin: "",
    agreePerformanceBased: false,
    agreeNoSpam: false,
    agreeFollowRules: false,
    agreeAcceptConsequences: false,
    outlets: {},
    password: "",
  });

  const steps = [
    { id: 0, title: "Referral", description: "Referral code" },
    { id: 1, title: "Account Type", description: "Choose your account type" },
    {
      id: 2,
      title: "Personal Info",
      description:
        accountType === "company" ? "Company details" : "Personal details",
    },
    { id: 3, title: "Promotion Channels", description: "How you promote" },
    { id: 4, title: "Content & Audience", description: "Your content profile" },
    { id: 5, title: "Experience", description: "Your background" },
    { id: 6, title: "Compliance", description: "Terms & agreements" },
    { id: 7, title: "Security", description: "Create password" },
  ];

  // Helper function to check if outlet array has meaningful data
  const hasOutletData = (outletArray, requiredFields) => {
    if (!outletArray || outletArray.length === 0) return false;
    return outletArray.some((item) =>
      requiredFields.some((field) => item[field]?.trim())
    );
  };

  // Function to create properly structured payload for backend
  const createRegistrationPayload = () => {
    // Normalize phone number to always start with 233
    let cleanPhone = null;
    
    if (formData.phone) {
      // Remove all non-digits
      let phoneDigits = formData.phone.replace(/[^\d]/g, "");
      
      // Handle different input formats
      if (phoneDigits.startsWith("233")) {
        // Already has country code, use as-is
        cleanPhone = phoneDigits;
      } else if (phoneDigits.startsWith("0")) {
        // Remove leading 0 and add 233
        cleanPhone = "233" + phoneDigits.substring(1);
      } else {
        // Local number without 0, add 233
        cleanPhone = "233" + phoneDigits;
      }
    }

    // Debug logging
    console.log("Phone submission debug:", {
      originalPhone: formData.phone,
      phoneCountry: formData.phoneCountry,
      normalizedPhone: cleanPhone
    });

    const payload = {
      // Referral (first step)
      referral_code:
        formData.hasReferralCode === true
          ? (formData.referralCode?.trim() || "")
          : "",

      // Account Type
      account_type: accountType,

      // Personal/Company Information
      first_name: formData.firstName?.trim() || null,
      last_name: formData.lastName?.trim() || null,
      email: formData.email?.trim() || null,
      phone_number: cleanPhone,
      password: formData.password || null,
      password_confirmation: formData.password || null,

      // Account Display Information
      account_display_name: formData.accountDisplayName?.trim() || null,

      // Company-specific fields (only for company accounts)
      ...(accountType === "company" && {
        company_website: formData.companyWebsite?.trim() || null,
        linkedin_page: formData.linkedinPage?.trim() || null,
      }),

      // Address Information
      address: {
        street_address_1: formData.streetAddress1?.trim() || null,
        street_address_2: formData.streetAddress2?.trim() || null,
        city: formData.city?.trim() || null,
        region: formData.region?.trim() || null,
        zip_code: formData.zipCode?.trim() || null,
        country: formData.country?.trim() || null,
      },

      // Location & Currency
      timezone: formData.timezone || null,
      currency: formData.currency || null,

      // ID Type & Number (required before first payout)
      id_type: formData.idType?.trim() || null,
      id_number: formData.idNumber?.trim() || null,
      other_id_name: formData.idType === "other" ? formData.otherIdName?.trim() || null : null,

      // Promotion Channels (New structured format)
      promotion_channels: formData.promotionChannels || null,

      // Content & Audience Profile
      content_categories: formData.contentCategories || null,
      target_audiences: formData.targetAudiences || null,
      posting_frequency: formData.postingFrequency || null,
      weekly_post_count: formData.weeklyPostCount || null,

      // Experience & Motivation
      has_affiliate_experience: formData.hasAffiliateExperience,
      previous_affiliate_platforms: formData.previousAffiliatePlatforms?.trim() || null,
      motivation_to_join: formData.motivationToJoin?.trim() || null,

      // Compliance & Declaration
      agree_performance_based: formData.agreePerformanceBased,
      agree_no_spam: formData.agreeNoSpam,
      agree_follow_rules: formData.agreeFollowRules,
      agree_accept_consequences: formData.agreeAcceptConsequences,

      // Promotion Methods/Outlets - Individual Fields (Legacy support)
      website: hasOutletData(formData.outlets?.website, ["name", "url"])
        ? formData.outlets.website
        : null,
      social: hasOutletData(formData.outlets?.social, [
        "platform",
        "handle",
        "followers",
      ])
        ? formData.outlets.social
        : null,
      mobile_app: hasOutletData(formData.outlets?.mobileApp, ["name", "appUrl"])
        ? formData.outlets.mobileApp
        : null,
      podcast: hasOutletData(formData.outlets?.podcast, ["name", "url"])
        ? formData.outlets.podcast
        : null,
      email_channels: hasOutletData(formData.outlets?.newsletter, [
        "name",
        "subscribers",
      ])
        ? formData.outlets.newsletter
        : null,
      offline: hasOutletData(formData.outlets?.offline, ["name", "type"])
        ? formData.outlets.offline
        : null,
    };

    return payload;
  };

  const handleNext = async () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsSubmitting(true);
      setSubmitError(null);
      const registrationPayload = createRegistrationPayload();
      try {
        const response = await authService.register(registrationPayload);

        if (response.status) {
          // Store user data temporarily for verification process
          localStorage.setItem("dbx_aff_temp_user", JSON.stringify(response.data));
          localStorage.setItem("dbx_aff_verification_email", formData.email);
          
          // Update Redux store with user data for verification process
          dispatch(setUserFromVerification(response.data));
          
          navigate("/verify-email", {
            state: {
              email: formData.email,
              userData: response.data,
            },
          });
        } else {
          setSubmitError(
            response.message || "Registration failed. Please try again."
          );
        }
      } catch (error) {
        setSubmitError("Something went wrong. Please try again.");
        console.error("Registration error:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAccountTypeSelected = (type) => {
    setAccountType(type);
    setCurrentStep(2);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <ReferralCodeStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 1:
        return (
          <AccountTypePrelude
            onAccountTypeSelected={handleAccountTypeSelected}
          />
        );
      case 2:
        return (
          <Step1Enhanced
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
            accountType={accountType}
          />
        );
      case 3:
        return (
          <Step2Enhanced
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 4:
        return (
          <Step3Enhanced
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 5:
        return (
          <Step4Enhanced
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 6:
        return (
          <Step5Enhanced
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 7:
        return (
          <Step6
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      default:
        return (
          <ReferralCodeStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
      <div className="overflow-hidden fixed inset-0">
        <img
          src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
          alt="Background"
          className="object-cover w-full h-full opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-red-50/80 via-orange-50/85 to-yellow-50/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r animate-pulse from-red-500/5 via-orange-500/5 to-yellow-500/5"></div>
      </div>

      {/* Main Container */}
      <div className="relative z-10 w-full min-h-screen">
        <div className="grid grid-cols-1 min-h-screen lg:grid-cols-3 lg:items-center">
          {/* Left Side - Registration Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col min-h-screen bg-white lg:h-screen lg:col-span-2"
          >
            <div className="overflow-y-auto flex-1 pb-4 scrollbar-thin scrollbar-thumb-red-400 scrollbar-track-transparent hover:scrollbar-thumb-red-500">
              {/* Header */}
              <div className="p-6 mb-4 text-left lg:p-8">
                <img src={Images.logo} alt="Dealboxx Logo" className="h-auto" />
              </div>

              {/* Progress Steps */}
              <div className="px-6 mb-4 lg:px-8">
                <div className="flex justify-between items-start mb-3">
                  {steps.map((step, index) => (
                    <div
                      key={step.id}
                      className="flex relative flex-col items-center"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                          currentStep >= step.id
                            ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25"
                            : "bg-gray-200 text-gray-600 border border-gray-300"
                        }`}
                      >
                        {currentStep > step.id ? "✓" : step.id + 1}
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 + index * 0.1 }}
                        className="mt-2 text-center"
                      >
                        <h4 className="text-xs font-medium text-gray-700">
                          {step.title}
                        </h4>
                      </motion.div>
                      {index < steps.length - 1 && (
                        <div
                          className={`absolute top-5 left-full w-full h-0.5 ${
                            currentStep > step.id
                              ? "bg-gradient-to-r from-red-500 to-red-600"
                              : "bg-gray-300"
                          }`}
                          style={{ width: "calc(100% - 2.5rem)" }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 lg:px-8"
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Sign In Link - Always visible at bottom */}
            <div className="flex-shrink-0 px-6 py-3 text-center bg-gray-50 border-t border-gray-200 lg:px-8">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-medium text-red-600 transition-colors hover:text-red-700"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Right Side*/}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:justify-center lg:items-center lg:flex lg:flex-col lg:h-90 lg:p-4"
          >
            <div className="flex overflow-hidden flex-col p-6 h-full bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-2xl border shadow-2xl backdrop-blur-lg border-red-200/50">
              <div className="flex-shrink-0 mb-3 text-center">
                <h2 className="mb-1 text-lg font-bold text-red-800">
                  Why Choose Dealboxx?
                </h2>
                <p className="text-xs text-red-700">
                  Join thousands of successful affiliates earning with our
                  platform
                </p>
              </div>

              <div className="overflow-y-auto flex-1 space-y-4 min-h-0 scrollbar-thin scrollbar-thumb-red-400 scrollbar-track-transparent hover:scrollbar-thumb-red-500">
                {[
                  {
                    icon: "💰",
                    title: "High Commissions",
                    description: "Earn up to 30% commission on every sale",
                  },
                  {
                    icon: "🚀",
                    title: "Fast Payouts",
                    description: "Get paid weekly with instant withdrawals",
                  },
                  {
                    icon: "📊",
                    title: "Real-time Analytics",
                    description:
                      "Track your performance with real-time analytics",
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    className="flex items-start p-3 space-x-3 rounded-lg border shadow-sm backdrop-blur-sm bg-white/80 border-red-200/50"
                  >
                    <div className="text-2xl">{feature.icon}</div>
                    <div>
                      <h3 className="mb-1 text-sm font-semibold text-red-800">
                        {feature.title}
                      </h3>
                      <p className="text-xs text-red-600">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="flex-shrink-0 mt-2 text-center"
              >
                <div className="p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-lg border border-red-400 shadow-lg backdrop-blur-sm">
                  <h3 className="mb-1 text-sm font-semibold text-white">
                    Join 10,000+ Affiliates
                  </h3>
                  <p className="mb-2 text-xs text-red-100">
                    Start earning today with our proven system
                  </p>
                  <div className="flex justify-center space-x-2 text-white">
                    <div className="text-center">
                      <div className="text-lg font-bold">GHS2M+</div>
                      <div className="text-xs">Paid Out</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">50K+</div>
                      <div className="text-xs">Deals</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">99%</div>
                      <div className="text-xs">Satisfaction</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Register;
